export type Tier = 1 | 2 | 3;
export type LeadStatus = 'new' | 'contacted' | 'follow_up' | 'qualified' | 'won' | 'lost' | 'invalid_lead';
export type ConsultationStage =
  | 'requested'
  | 'booked'
  | 'confirmed'
  | 'attended'
  | 'missed'
  | 'cancelled'
  | 'rescheduled'
  | 'treatment_qualified'
  | 'treatment_started';
export type QualificationOutcome = 'positive' | 'booked' | 'declined' | 'pending';
export type QualificationOutcomeRecord = {
  lead_id: string;
  outcome: QualificationOutcome;
  reason_code: string;
  reason_text: string | null;
  next_action_at: string | null;
  actor: string;
  recorded_at: string;
  updated_at: string;
};
export type AppointmentSlot = { start_time: string; end_time: string | null };
export type AssignedFilter = '' | 'me' | 'unassigned' | 'all';

export const STATUSES: LeadStatus[] = ['new', 'contacted', 'follow_up', 'qualified', 'won', 'lost', 'invalid_lead'];

export const STATUS_LABEL: Record<LeadStatus, string> = {
  new: 'New',
  contacted: 'Contacted',
  follow_up: 'Follow Up',
  qualified: 'Qualified',
  won: 'Won',
  lost: 'Lost',
  invalid_lead: 'Rejected',
};

export const STATUS_COLOR: Record<LeadStatus, string> = {
  new: 'bg-slate-200 text-slate-800',
  contacted: 'bg-blue-100 text-blue-800',
  follow_up: 'bg-yellow-100 text-yellow-800',
  qualified: 'bg-purple-100 text-purple-800',
  won: 'bg-emerald-100 text-emerald-800',
  lost: 'bg-rose-100 text-rose-800',
  invalid_lead: 'bg-slate-200 text-slate-700',
};

export type Lead = {
  id: string;
  name: string;
  phone: string;
  raw_answers: string | null;
  email?: string | null;
  created_at: string;
  tier_score: Tier;
  persuasion_tip: string;
  opening_line: string;
  status: LeadStatus;
  assigned_to: string | null;
  last_assigned_to?: string | null;
  last_assigned_at?: string | null;
  last_activity_at?: string | null;
  updated_at: string;
  source: string;
  whatsapp_number_used?: string;
  clinic_tag?: string;
  tags?: string | string[];
  call_outcome?: 'answered' | 'missed' | null;
  phone_identity?: string;
  is_blocked?: boolean;
  source_history?: string | string[] | null;
  latest_source?: string | null;
  returned_lead?: boolean | number;
  returned_at?: string | null;
  returned_from_status?: string | null;
  returned_from_lost_at?: string | null;
  returned_from_lost_reason?: string | null;
  consultation_date?: string | null;
  consultation_time?: string | null;
  consultation_mode?: 'Clinic' | 'Phone' | 'Video' | string | null;
  consultation_location?: string | null;
  consultation_status?: ConsultationStage | string | null;
  consultation_booked_at?: string | null;
  consultation_attended_at?: string | null;
  consultation_missed_at?: string | null;
  consultation_cancelled_at?: string | null;
  consultation_rescheduled_at?: string | null;
  treatment_qualified_at?: string | null;
  treatment_started_at?: string | null;
  consultation_updated_at?: string | null;
  consultation_updated_by?: string | null;
  consultation_requested_at?: string | null;
  consultation_requested_by?: string | null;
  consultation_confirmation_status?: 'pending' | 'confirmed' | string | null;
  consultation_confirmed_at?: string | null;
  consultation_confirmed_by?: string | null;
  consultation_confirmation_source?: 'Practo' | 'Clinic register' | 'CRM manual' | string | null;
  consultation_confirmation_reference?: string | null;
  consultation_appointment_id?: string | null;
  verified_payment_amount_minor?: number | null;
  verified_payment_reference?: string | null;
  verified_payment_at?: string | null;
};

export function parseLeadTags(value: Lead['tags']): string[] {
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value !== 'string') return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch {
    return [];
  }
}

export type WhatsAppInboxItem = {
  id: string;
  phone_identity: string;
  whatsapp_number_used: string;
  current_lead_id: string | null;
  assigned_to: string | null;
  needs_reply: number;
  unread_count: number;
  last_inbound_message_id: string | null;
  last_inbound_preview: string | null;
  last_inbound_at: string | null;
  first_unanswered_at: string | null;
  name: string | null;
  status: string | null;
  // The patient's WATI chatbot choice; waiting = no human or WATI-operator reply since their latest message.
  patient_request?: 'book' | 'call' | null;
  patient_request_waiting?: number;
};

// Mirrors worker/src/lib/call-result.ts; the worker decides the classification.
export type CallResultRecord = {
  call_session_id: string;
  started_at: string | null;
  kind: 'in_progress' | 'connected' | 'patient' | 'ended_early' | 'technical' | 'unconfirmed';
  code: string;
  title: string;
  detail: string;
  final: boolean;
  duration_seconds?: number;
};

export type LeadAttribution = {
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  ad_id: string | null;
  ad_name: string | null;
  ctwa_clid: string | null;
  referral_source_id: string | null;
  provider_message_id: string | null;
  provider_conversation_id: string | null;
  platform: string | null;
  source: string | null;
  channel: string | null;
  form_id: string | null;
  service_urn: string | null;
  resolved_via: string | null;
  metadata: string | null;
  updated_at: string | null;
};

export type LeadReportedSource = {
  category: 'meta' | 'google' | 'referral' | 'doctor_or_clinic' | 'existing_patient' | 'offline' | 'does_not_remember' | 'other';
  detail: string | null;
  actor: string;
  created_at?: string | null;
};

export type SocialInboxStatus = 'new' | 'assigned' | 'waiting' | 'replied' | 'closed' | 'noise';
export type SocialInboxType = 'dm' | 'comment' | 'review' | 'mention';

export type SocialInboxConversation = {
  id: string;
  platform: string;
  type: SocialInboxType;
  author_id: string | null;
  author_name: string | null;
  latest_message_preview: string | null;
  last_message_at: string;
  status: SocialInboxStatus;
  is_read: number;
  unread_count: number;
  assignee: string | null;
  matched_lead_id: string | null;
  referral_ad_id: string | null;
  parent_id: string | null;
  parent_url: string | null;
  account_id: string | null;
  capabilities: string;
  post_id?: string | null;
  has_human_reply?: number;
  has_automation_reply?: number;
  last_inbound_at?: string | null;
  last_human_reply_at?: string | null;
  last_human_reply_source?: 'crm' | 'external' | null;
  last_human_reply_by?: string | null;
  created_at: string;
  updated_at: string;
};

export type SocialInboxMessage = {
  id: string;
  conversation_id: string;
  platform: string;
  type: SocialInboxType;
  direction: 'inbound' | 'outbound';
  provider_message_id?: string | null;
  delivery_status?: string | null;
  send_source?: 'crm' | 'external' | 'automation' | null;
  is_automation?: number;
  author: {
    id: string;
    name: string;
  };
  content: {
    text: string;
    media: unknown[];
  };
  message_at: string;
  source?: string;
};

export type SocialInboxSummary = {
  pending_count: number;
  total: number;
  status_counts: Record<string, number>;
  platform_counts: Record<string, number>;
  type_counts: Record<string, number>;
};

export type SocialInboxListResponse = {
  items: SocialInboxConversation[];
  has_more: boolean;
  next_cursor: string | null;
  summary: SocialInboxSummary;
};

export type SocialInboxDetailResponse = {
  conversation: SocialInboxConversation;
  messages: SocialInboxMessage[];
  disposition?: InteractionDisposition | null;
  external_reply_detected?: boolean;
  post?: {
    id: string;
    platform: string;
    account_id: string;
    platform_post_id: string;
    caption: string | null;
    media_url: string | null;
    thumbnail_url: string | null;
    media_type: string | null;
    permalink: string | null;
    published_at: string | null;
  } | null;
};

export type InteractionOutcome = 'awaiting_patient' | 'timed_follow_up' | 'positive_probable' | 'appointment' | 'lost' | 'rejected';

export type InteractionDisposition = {
  id: string;
  interaction_key: string;
  channel: string;
  provider: string;
  lead_id: string | null;
  conversation_id: string | null;
  owner: string | null;
  status: 'pending' | 'completed';
  outcome: InteractionOutcome | null;
  evidence_at: string;
};

export type ConversationAssist = {
  intent: 'consultation' | 'appointment' | 'cost' | 'location' | 'treatment_info' | 'follow_up' | 'not_relevant' | 'unclear';
  confidence: number;
  summary?: string;
  stage?: 'new_inquiry' | 'exploring' | 'interested' | 'ready_to_book' | 'booked' | 'follow_up_needed' | 'not_interested' | 'wrong_number_or_spam';
  qualification_hint?: 'likely_positive' | 'booked' | 'declined' | 'undecided';
  reply_guarded?: boolean;
  cached?: boolean;
  rationale: string;
  next_action: string;
  suggested_reply: string;
  provider: 'groq' | 'workers-ai' | 'fallback';
  model: string;
  prompt_version: string;
  latest_message_id: string;
};

export type LeadQueueSummary = {
  total: number;
  hot: number;
  warm: number;
  cold: number;
  sources: Record<string, number>;
};

export type LeadEvent = {
  id: string;
  kind: string;
  actor: string | null;
  data: string | null;
  created_at: string;
};

export type CareMilestone =
  | 'lead_received'
  | 'qualified_lead'
  | 'consult_booked'
  | 'consult_showed'
  | 'treatment_qualified'
  | 'cycle_started'
  | 'revenue';

export const CARE_MILESTONES: CareMilestone[] = [
  'lead_received',
  'qualified_lead',
  'consult_booked',
  'consult_showed',
  'treatment_qualified',
  'cycle_started',
  'revenue',
];

export const CARE_MILESTONE_LABEL: Record<CareMilestone, string> = {
  lead_received: 'Lead received',
  qualified_lead: 'Qualified',
  consult_booked: 'Consult booked',
  consult_showed: 'Consult showed',
  treatment_qualified: 'Treatment qualified',
  cycle_started: 'Cycle started',
  revenue: 'Revenue',
};

export type LeadOutcome = {
  id: string;
  lead_id: string;
  milestone: CareMilestone;
  actor: string | null;
  note: string | null;
  value_amount: number | null;
  currency: string;
  occurred_at: string;
  created_at: string;
  updated_at: string;
};

export type CallLog = {
  id: string;
  duration: number;
  recording_url: string | null;
  initiated_by: string | null;
  created_at: string;
};

export type Telecaller = {
  email: string;
  name: string | null;
  phone: string | null;
  center: 'BBSR' | 'BAM' | 'Angule' | null;
  active: number;
  last_seen: string | null;
};

export type CallQueueItem = {
  id: string;
  callUuid: string;
  customerPhone: string;
  campaign: string;
  leadId?: string | null;
  leadName?: string | null;
  leadPhone?: string | null;
  previousCallCount?: number;
  lastAttendedBy?: string | null;
  existingLead?: boolean;
  status: string;
  createdAt: string;
  expiresAt: string;
  assignedAgentId?: string | null;
  offeredAt?: string | null;
  offerAttempt?: number;
  rejectedAgents?: string | null;
};

export type Me = {
  id: string;
  email: string;
  name: string | null;
  phone: string | null;
  role: 'super_admin' | 'admin' | 'manager' | 'telecaller';
  active: number;
  created_at?: string;
  last_seen?: string | null;
};

export type Insights = {
  scope: 'team' | 'me';
  totals: { total_leads: number; total_calls: number; call_attempts?: number; connected_calls?: number; total_seconds: number; unassigned: number };
  today: { new_leads: number; new_lead_calls: number; calls_made: number; call_attempts?: number; total_attempts: number; total_calls?: number; connected_calls?: number; wins_today: number; registrations_today?: number | null; whatsapp_leads?: number };
  by_status: Record<string, number>;
  by_tier: Record<string, number>;
  by_source: Record<string, number>;
  by_campaign: Record<string, number>;
  source_funnels: {
    whatsapp: { total: number; reached: number; qualified: number; positive: number };
    social: { total: number; reached: number; qualified: number; positive: number };
    call_signals: { total_signals: number; contacted: number; qualified: number; positive: number };
  };
  conversion_rate: number;
  top_telecallers: { assigned_to: string; n: number; name?: string; count?: number }[];
  manager_report?: {
    agent_performance: Array<{
      email: string;
      name: string;
      role: string;
      active: number;
      last_login_at: string | null;
      current_status: 'available' | 'busy' | 'break' | 'offline' | 'on_call' | string;
      current_call_uuid: string | null;
      last_status_at: string | null;
      minutes_since_seen: number | null;
      status_is_stale: boolean;
      attendance_state: 'online' | 'on_break' | 'offline' | string;
      active_leads: number;
      hot_leads: number;
      warm_leads: number;
      calls_today: number;
      calls_7d: number;
      connected_calls_7d: number;
      missed_calls_7d: number;
      avg_duration_7d: number;
      wins_7d: number;
      lost_7d: number;
      due_followups: number;
      overdue_followups: number;
      unique_whatsapp_patients_replied_today: number;
    }>;
    attendance_summary: {
      total: number;
      available: number;
      busy: number;
      on_call: number;
      break: number;
      offline: number;
      stale: number;
    };
    followups: {
      pending_due: number;
      overdue: number;
      due_today: number;
      pending_total: number;
    };
    disposition_reasons: Array<{
      status: string;
      reason: string;
      count: number;
    }>;
    dispositions: {
      total_disposed_count: number;
      disposed_connected_count: number;
      disposed_not_connected_count: number;
      whatsapp_contact_count: number;
      // Legacy: leads marked Won today (status). registered_today is the CRM definition of won.
      converted_today: number;
      registered_today?: number | null;
    };
  };
};

export type MorningSummary = {
  success: boolean;
  metric_version?: number;
  expires_at?: string;
  generated_at: string;
  timezone: 'Asia/Kolkata';
  scope: 'team' | 'me';
  whatsapp_text: string;
  metrics: {
    today: { date: string; new_leads: number; calls_made: number; wins: number };
    yesterday: { date: string; new_leads: number; calls_made: number; wins: number };
    queue: { unassigned: number | null; followups: number; hot: number; warm: number };
    last_7_days: {
      start: string;
      end: string;
      total_leads: number;
      qualified_leads: number;
      attributed_leads: number;
      attribution_rate_pct: number | null;
    };
    top_sources: unknown[];
    top_campaigns: unknown[];
    review_analysis?: PublicReviewAnalysis;
  };
  signals: Array<{
    source_system: string;
    title: string;
    summary: string;
    recommended_action: string;
    priority: string;
    campaign_name: string;
    created_at: string;
  }>;
  actions: Array<{ owner: string; task: string; reason: string }>;
  data_gaps: string[];
};

export type JourneySummary = {
  total_leads: number;
  assigned: number;
  called: number;
  connected: number;
  qualified: number;
  won: number;
  // Optional: absent in snapshots saved before schema 12.
  qualified_positive?: number;
  registered?: number;
  unassigned: number;
  not_called: number;
  not_connected: number;
  no_patient_outcome?: number;
  lost: number;
  still_open: number;
};

export type AttributionRow = {
  source: string;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  total_leads: number;
  assigned: number;
  unassigned: number;
  called: number;
  not_called: number;
  connected: number;
  not_connected: number;
  no_patient_outcome?: number;
  qualified: number;
  positive?: number;
  won: number;
  registered?: number;
  lost: number;
  open: number;
  connection_rate: number;
  conversion_rate: number;
  registration_rate?: number;
  rejection_rate: number;
  won_reasons: Array<{ reason: string; count: number }>;
  lost_reasons: Array<{ reason: string; count: number }>;
};

export type AttributionSummary = {
  campaigns: AttributionRow[];
  adsets: AttributionRow[];
  ads: AttributionRow[];
};

export type ExecutiveInsight = {
  type: 'top_converting_ads' | 'highest_rejection_ads' | 'low_contact_rate' | 'high_connection_low_conversion' | 'unworked_ads';
  title: string;
  details: Array<{
    label: string;
    total_leads: number;
    metric_value: number;
    metric_label: string;
  }>;
};

// ── Morning Management Report types ────────────────────────────────────────

export type NeedsAttention = {
  yesterday_unassigned: number;
  yesterday_not_called: number;
  yesterday_not_connected: number;
  followups_due_today: number;
  followups_overdue: number;
  hot_pending: number;
  warm_pending: number;
};

export type PriorityAction = {
  text: string;
  priority: 'high' | 'medium' | 'low';
};

export type OperationalDiagnosis = {
  campaign: string;
  total_leads: number;
  assigned: number;
  called: number;
  connected: number;
  won: number;
  verdict: 'healthy' | 'operational_issue' | 'marketing_issue' | 'closing_gap' | 'insufficient_data';
  notes: string;
};

export type WorkedLeadOutcome = {
  total_attempts: number;
  unique_worked_leads: number;
  connected: number;
  not_connected: number;
  no_patient_outcome?: number;
  registered_today?: number;
  won_today: number;
  lost_today: number;
  qualified_today: number;
  follow_up_today: number;
  current_won: number;
  current_lost: number;
  current_qualified: number;
  current_follow_up: number;
  current_open: number;
  work_type_breakdown: Array<{
    type: string;
    label: string;
    attempts: number;
    unique: number;
    connected: number;
    not_connected: number;
    won: number;
    lost: number;
  }>;
  lost_reasons_today: Array<{ reason: string; count: number; percentage: number }>;
  won_reasons_today: Array<{ reason: string; count: number; percentage: number }>;
  lost_total_today: number;
  won_total_today: number;
};

export type WorkedAttributionRow = {
  source: string;
  campaign_id: string | null;
  campaign_name: string | null;
  adset_id: string | null;
  adset_name: string | null;
  ad_id: string | null;
  ad_name: string | null;
  worked_leads: number;
  connected: number;
  not_connected: number;
  won: number;
  registered?: number;
  lost: number;
  follow_up: number;
  open: number;
  connection_rate: number;
  conversion_rate: number;
  registration_rate?: number;
  rejection_rate: number;
  lost_reasons: Array<{ reason: string; count: number }>;
  won_reasons: Array<{ reason: string; count: number }>;
};

export type WorkedLeadOutcomes = {
  outcome: WorkedLeadOutcome;
  attribution: {
    campaigns: WorkedAttributionRow[];
    adsets: WorkedAttributionRow[];
    ads: WorkedAttributionRow[];
  };
  insights: ExecutiveInsight[];
};

export type AdminAnalyticsChartData = Record<string, unknown> & {
  journey_summary?: JourneySummary;
  yesterday_activity?: {
    totalCalls: number;
    contactedYesterday: number;
    newLeadContacted: number;
    qualified: number;
    booked: number;
    won: number;
    lost: number;
  };
  attribution_summary?: AttributionSummary;
  executive_insights?: ExecutiveInsight[];
  needs_attention?: NeedsAttention;
  priority_actions?: PriorityAction[];
  operational_diagnosis?: OperationalDiagnosis[];
  social_activity?: {
    whatsapp_leads: number;
    reviews: number;
    instagram_comments: number;
    facebook_comments?: number;
    instagram_messages: number;
    facebook_messages?: number;
    instagram_replies?: number;
    facebook_replies?: number;
    social_messages?: number;
    social_replies?: number;
    mentions?: number;
  };
  whatsapp_engagement?: {
    leads_received: number;
    patient_responded_back: number;
    organic: number;
    meta_ctwa: number;
  };
  brief_snapshot?: {
    queue?: { unassigned: number; followups: number; overdue: number; hot: number; warm: number };
  };
  whatsapp_brief?: string;
  cohort_date?: string;
  report_date?: string;
  status_reason_breakdown?: Array<{ status: string; total: number; reasons: Array<{ reason: string; count: number; percentage: number }> }>;
  pipeline?: Array<{ status: string; count: number }>;
  pipeline_total?: number;
  worked_lead_outcomes?: WorkedLeadOutcomes;
};

export type AdminAnalyticsReport = {
  id: string;
  report_type: 'eod' | 'morning';
  window_start: string;
  window_end: string;
  status: 'pending' | 'generating' | 'completed' | 'failed';
  generated_at: string | null;
  generation_started_at: string | null;
  previous_generated_at?: string | null;
  regenerated_at?: string | null;
  regenerated_by?: string | null;
  generation_error: string | null;
  schema_version: number;
  created_at: string;
  updated_at: string;
  overall_summary: Record<string, number>;
  telecaller_summary: Array<Record<string, string | number>>;
  source_summary: Array<Record<string, string | number>>;
  chart_data: AdminAnalyticsChartData;
};

export type Tab = 'unassigned' | 'queue' | 'leads' | 'call-logs' | 'insights';

export type IvrOption = {
  digit: string;
  label: string;
  topic: string;
  tier: 1 | 2 | 3;
  tip: string;
  opening_line: string;
  auto_wa_template?: string;
};

export type IvrConfig = {
  campaign: string;
  display_name: string | null;
  greeting: string;
  greeting_audio_url: string | null;
  thank_you_audio_url: string | null;
  options_json: string;
  no_input_tier: number;
  no_input_topic: string;
  no_input_tip: string | null;
  no_input_opening: string | null;
  voice: 'woman' | 'man';
  active: number;
  business_hours_start?: string;
  business_hours_end?: string;
  business_days?: string;
  timezone?: string;
  after_hours_greeting?: string | null;
  after_hours_greeting_audio_url?: string | null;
  after_hours_options_json?: string;
  support_phone?: string | null;
  created_at: string;
  updated_at: string;
};

export type MetaPage = {
  page_id: string;
  name: string | null;
  active: number;
  created_at: string;
  updated_at: string;
  last_used: string | null;
  token_preview: string;
};

export type PatientFeedbackInput = {
  name: string;
  phone: string;
  visitDate: string;
  center?: string | null;
  doctor?: string | null;
};

export type PatientFeedbackPreviewRow = PatientFeedbackInput & {
  rowIndex: number;
  normalizedPhone: string | null;
  status: 'valid' | 'invalid' | 'duplicate';
  issues: string[];
  existingSendCount?: number;
};

export type PatientFeedbackPreviewResult = {
  total: number;
  valid: number;
  invalid: number;
  duplicates: number;
  rows: PatientFeedbackPreviewRow[];
};

export type PatientFeedbackSendRow = PatientFeedbackInput & {
  englishStatus: 'sent' | 'failed' | 'blocked_duplicate' | 'invalid';
  odiaStatus: 'sent' | 'failed' | 'blocked_duplicate' | 'invalid';
  error: string | null;
  englishResponse: string | null;
  odiaResponse: string | null;
  duplicate: boolean;
  batchId: string | null;
};

export type PatientFeedbackSendResult = {
  success: boolean;
  total: number;
  sent: number;
  failed: number;
  duplicate: number;
  results: PatientFeedbackSendRow[];
  batchId: string;
};

export type PublicReview = {
  id: string;
  source: string;
  source_review_id: string;
  location_name: string | null;
  author_name: string | null;
  rating: number | null;
  published_label: string | null;
  review_text: string | null;
  reply_status: 'unreplied' | 'drafted' | 'replied' | 'manual';
  reply_text: string | null;
  draft_reply: string | null;
  replied_at: string | null;
  sentiment: 'positive' | 'neutral' | 'negative' | 'unknown';
  theme_tags: string;
  risk_flags: string;
  private_followup_status: 'not_needed' | 'needs_review' | 'in_progress' | 'resolved';
  service_recovery_note: string | null;
  mos_sync_status: string;
  mos_synced_at: string | null;
  created_at: string;
  updated_at: string;
};

export type PublicReviewSummary = {
  total: number;
  needs_reply: number;
  negative: number;
  needs_recovery: number;
  mos_synced: number;
};

export type PublicReviewAnalysis = {
  total: number;
  positive: number;
  negative: number;
  needs_reply: number;
  needs_recovery: number;
  mos_synced: number;
  top_themes: Array<{ theme: string; label: string; count: number }>;
  content_urn: string;
  tracking_url: string;
  campaign_tag: string;
  recommended_hashtags: string[];
  post_footer: string;
  publish_checklist: string[];
  organic_post_draft: string;
  meta_campaign_angle: string;
  meta_copy_prompt: string;
  safety_notes: string[];
};

export type PublicReviewsResponse = {
  summary: PublicReviewSummary;
  analysis: PublicReviewAnalysis;
  reviews: PublicReview[];
};

export type WaTemplate = {
  name: string;
  label: string;
  category: 'MARKETING' | 'UTILITY' | 'AUTHENTICATION';
  status: 'APPROVED' | 'PENDING' | 'REJECTED';
  language: string;
  params: string[];
  has_button?: boolean;
  has_media?: 'video' | 'image' | 'document';
  note?: string;
};

export type WhatsAppMessageRecord = {
  id: string;
  provider_message_id: string | null;
  client_temp_id?: string | null;
  direction: 'inbound' | 'outbound';
  text: string;
  type: 'text' | 'image' | 'document' | 'video' | 'audio' | 'other';
  timestamp: string;
  status: 'sending' | 'submitted' | 'sent' | 'delivered' | 'read' | 'failed' | null;
  sender_name?: string;
  media?: { url?: string; file_name?: string; mime_type?: string };
};

export type Followup = {
  id: string;
  lead_id: string;
  followup_date: string;
  followup_time: string | null;
  notes: string | null;
  status: 'pending' | 'completed' | 'cancelled';
  created_by: string;
  created_at: string;
  completed_at: string | null;
};

export type DashboardSnapshotType = 'morning' | 'eod';
export type DashboardSnapshotStatus = 'generating' | 'ready' | 'failed';
export type DashboardSnapshot = {
  id: string;
  snapshot_date: string;
  snapshot_type: DashboardSnapshotType;
  status: DashboardSnapshotStatus;
  summary_json: string | null;
  source_breakdown_json: string | null;
  status_breakdown_json: string | null;
  agent_breakdown_json: string | null;
  funnel_json: string | null;
  sla_metrics_json: string | null;
  quality_metrics_json: string | null;
  recommended_actions_json: string | null;
  generated_by: string | null;
  generated_at: string | null;
  generation_duration_ms: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
};
